import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Chip, IconButton, Tooltip, Button, TextField,
  Select, MenuItem, FormControl, InputLabel, Dialog, DialogTitle,
  DialogContent, DialogActions, CircularProgress, Alert, Tabs, Tab,
  InputAdornment, Autocomplete
} from '@mui/material';
import {
  Link as LinkIcon,
  LinkOff as UnlinkIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Wifi as WifiIcon,
  Cable as CableIcon,
  Router as RouterIcon,
  Hub as SwitchIcon,
  DeviceUnknown as DeviceIcon,
  CheckCircle as LinkedIcon,
  Warning as UnlinkedIcon,
  FilterList as FilterIcon
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { unifiApi } from '../../../services/apiClient';

const deviceTypeIcon = (type) => {
  const icons = {
    'access_point': <WifiIcon fontSize="small" />,
    'switch': <SwitchIcon fontSize="small" />,
    'gateway': <RouterIcon fontSize="small" />,
    'dream_machine': <RouterIcon fontSize="small" />,
    'next_gen_gateway': <RouterIcon fontSize="small" />
  };
  return icons[type] || <DeviceIcon fontSize="small" />;
};

const statusChip = (status) => {
  const color = status === 'online' ? 'success' : status === 'offline' ? 'error' : 'default';
  return <Chip label={status} size="small" color={color} variant="outlined" />;
};

const UniFiDeviceLinking = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState(0); // 0 = devices, 1 = clients
  const [filter, setFilter] = useState('unlinked');
  const [search, setSearch] = useState('');
  const [data, setData] = useState({ unifiDevices: [], unifiClients: [], caroDevices: [], summary: {} });
  const [linkDialog, setLinkDialog] = useState({ open: false, type: null, item: null });
  const [selectedCaroDevice, setSelectedCaroDevice] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await unifiApi.getLinkingStatus(filter);
      if (res.data.success) {
        setData(res.data);
      }
    } catch (error) {
      enqueueSnackbar('Failed to load linking data', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [filter, enqueueSnackbar]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleLink = async () => {
    if (!linkDialog.item || !selectedCaroDevice) return;
    setActionLoading('link');
    try {
      const { type, item } = linkDialog;
      if (type === 'device') {
        await unifiApi.linkDevice(item.id, selectedCaroDevice.id);
      } else {
        await unifiApi.linkClient(item.id, selectedCaroDevice.id);
      }
      enqueueSnackbar(`Linked "${item.name}" to "${selectedCaroDevice.name}"`, { variant: 'success' });
      setLinkDialog({ open: false, type: null, item: null });
      setSelectedCaroDevice(null);
      fetchData();
    } catch (error) {
      enqueueSnackbar('Failed to link', { variant: 'error' });
    } finally {
      setActionLoading(null);
    }
  };

  const handleUnlink = async (type, item) => {
    setActionLoading(item.id);
    try {
      if (type === 'device') {
        await unifiApi.unlinkDevice(item.id);
      } else {
        await unifiApi.unlinkClient(item.id);
      }
      enqueueSnackbar(`Unlinked "${item.name}"`, { variant: 'success' });
      fetchData();
    } catch (error) {
      enqueueSnackbar('Failed to unlink', { variant: 'error' });
    } finally {
      setActionLoading(null);
    }
  };

  const filterItems = (items) => {
    if (!search) return items;
    const q = search.toLowerCase();
    return items.filter(i =>
      (i.name || '').toLowerCase().includes(q) ||
      (i.mac || '').toLowerCase().includes(q) ||
      (i.ip || '').toLowerCase().includes(q) ||
      (i.model || '').toLowerCase().includes(q) ||
      (i.linkedDevice?.name || '').toLowerCase().includes(q)
    );
  };

  const devices = filterItems(data.unifiDevices);
  const clients = filterItems(data.unifiClients);
  const summary = data.summary || {};

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h6">UniFi Device Linking</Typography>
        <Button startIcon={<RefreshIcon />} onClick={fetchData} disabled={loading} size="small">
          Refresh
        </Button>
      </Box>

      {/* Summary */}
      <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
        <Paper sx={{ p: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
          <RouterIcon color="action" />
          <Box>
            <Typography variant="caption" color="text.secondary">UniFi Devices</Typography>
            <Typography variant="body2">
              <Chip label={`${summary.devices?.linked || 0} linked`} size="small" color="success" variant="outlined" sx={{ mr: 0.5 }} />
              <Chip label={`${summary.devices?.unlinked || 0} unlinked`} size="small" color="warning" variant="outlined" />
            </Typography>
          </Box>
        </Paper>
        <Paper sx={{ p: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
          <WifiIcon color="action" />
          <Box>
            <Typography variant="caption" color="text.secondary">UniFi Clients</Typography>
            <Typography variant="body2">
              <Chip label={`${summary.clients?.linked || 0} linked`} size="small" color="success" variant="outlined" sx={{ mr: 0.5 }} />
              <Chip label={`${summary.clients?.unlinked || 0} unlinked`} size="small" color="warning" variant="outlined" />
            </Typography>
          </Box>
        </Paper>
      </Box>

      {/* Filters */}
      <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center' }}>
        <TextField
          size="small"
          placeholder="Search by name, MAC, IP, model..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
          sx={{ minWidth: 300 }}
        />
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Filter</InputLabel>
          <Select value={filter} onChange={(e) => setFilter(e.target.value)} label="Filter">
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="unlinked">Unlinked only</MenuItem>
            <MenuItem value="linked">Linked only</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {/* Tabs */}
      <Tabs value={tab} onChange={(e, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab label={`Devices (${devices.length})`} />
        <Tab label={`Clients (${clients.length})`} />
      </Tabs>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : tab === 0 ? (
        /* Devices Table */
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>Device</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Model</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>MAC / IP</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Site</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Linked CARO Device</TableCell>
                <TableCell sx={{ fontWeight: 600 }} align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {devices.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                      {filter === 'unlinked' ? 'All devices are linked' : 'No devices found'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : devices.map((device) => (
                <TableRow key={device.id} hover sx={{ bgcolor: !device.linkedDevice ? 'warning.50' : undefined }}>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {deviceTypeIcon(device.deviceType)}
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>{device.name}</Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{device.model || '—'}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" sx={{ fontFamily: 'monospace', display: 'block' }}>{device.mac}</Typography>
                    <Typography variant="caption" color="text.secondary">{device.ip || '—'}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption">{device.siteName}</Typography>
                  </TableCell>
                  <TableCell>{statusChip(device.status)}</TableCell>
                  <TableCell>
                    {device.linkedDevice ? (
                      <Chip
                        icon={<LinkedIcon />}
                        label={device.linkedDevice.name}
                        size="small"
                        color="success"
                        variant="outlined"
                      />
                    ) : (
                      <Chip icon={<UnlinkedIcon />} label="Not linked" size="small" color="warning" variant="outlined" />
                    )}
                  </TableCell>
                  <TableCell align="center">
                    {device.linkedDevice ? (
                      <Tooltip title="Unlink from CARO device">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleUnlink('device', device)}
                          disabled={actionLoading === device.id}
                        >
                          {actionLoading === device.id ? <CircularProgress size={18} /> : <UnlinkIcon fontSize="small" />}
                        </IconButton>
                      </Tooltip>
                    ) : (
                      <Tooltip title="Link to CARO device">
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => setLinkDialog({ open: true, type: 'device', item: device })}
                        >
                          <LinkIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        /* Clients Table */
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>Client</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Connection</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>MAC / IP</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Site</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Last Seen</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Linked CARO Device</TableCell>
                <TableCell sx={{ fontWeight: 600 }} align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {clients.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                      {filter === 'unlinked' ? 'All clients are linked' : 'No clients found'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : clients.map((client) => (
                <TableRow key={client.id} hover sx={{ bgcolor: !client.linkedDevice ? 'warning.50' : undefined }}>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>{client.name}</Typography>
                    {client.network && <Typography variant="caption" color="text.secondary">{client.network}</Typography>}
                  </TableCell>
                  <TableCell>
                    <Chip
                      icon={client.isWired ? <CableIcon /> : <WifiIcon />}
                      label={client.isWired ? 'Wired' : 'Wireless'}
                      size="small"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" sx={{ fontFamily: 'monospace', display: 'block' }}>{client.mac}</Typography>
                    <Typography variant="caption" color="text.secondary">{client.ip || '—'}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption">{client.siteName}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption">
                      {client.lastSeen ? new Date(client.lastSeen).toLocaleString() : '—'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {client.linkedDevice ? (
                      <Chip
                        icon={<LinkedIcon />}
                        label={client.linkedDevice.name}
                        size="small"
                        color="success"
                        variant="outlined"
                      />
                    ) : (
                      <Chip icon={<UnlinkedIcon />} label="Not linked" size="small" color="warning" variant="outlined" />
                    )}
                  </TableCell>
                  <TableCell align="center">
                    {client.linkedDevice ? (
                      <Tooltip title="Unlink from CARO device">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleUnlink('client', client)}
                          disabled={actionLoading === client.id}
                        >
                          {actionLoading === client.id ? <CircularProgress size={18} /> : <UnlinkIcon fontSize="small" />}
                        </IconButton>
                      </Tooltip>
                    ) : (
                      <Tooltip title="Link to CARO device">
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => setLinkDialog({ open: true, type: 'client', item: client })}
                        >
                          <LinkIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Link Dialog */}
      <Dialog open={linkDialog.open} onClose={() => { setLinkDialog({ open: false, type: null, item: null }); setSelectedCaroDevice(null); }} maxWidth="sm" fullWidth>
        <DialogTitle>
          Link {linkDialog.type === 'device' ? 'UniFi Device' : 'UniFi Client'} to CARO Device
        </DialogTitle>
        <DialogContent>
          {linkDialog.item && (
            <Alert severity="info" sx={{ mb: 2 }}>
              <strong>{linkDialog.item.name}</strong>
              <br />
              MAC: {linkDialog.item.mac} | IP: {linkDialog.item.ip || '—'}
              {linkDialog.item.model && <> | Model: {linkDialog.item.model}</>}
            </Alert>
          )}
          <Autocomplete
            options={data.caroDevices}
            getOptionLabel={(option) => `${option.name} (${option.mac || option.ip || 'ID: ' + option.id})`}
            value={selectedCaroDevice}
            onChange={(e, val) => setSelectedCaroDevice(val)}
            renderInput={(params) => (
              <TextField {...params} label="Select CARO Device" placeholder="Search by name, MAC, IP..." fullWidth margin="normal" />
            )}
            renderOption={(props, option) => (
              <li {...props} key={option.id}>
                <Box>
                  <Typography variant="body2">{option.name}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {option.mac || '—'} | {option.ip || '—'} | {option.status}
                  </Typography>
                </Box>
              </li>
            )}
            filterOptions={(options, { inputValue }) => {
              const q = inputValue.toLowerCase();
              return options.filter(o =>
                (o.name || '').toLowerCase().includes(q) ||
                (o.mac || '').toLowerCase().includes(q) ||
                (o.ip || '').toLowerCase().includes(q) ||
                String(o.id).includes(q)
              );
            }}
            isOptionEqualToValue={(option, value) => option.id === value.id}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setLinkDialog({ open: false, type: null, item: null }); setSelectedCaroDevice(null); }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleLink}
            disabled={!selectedCaroDevice || actionLoading === 'link'}
            startIcon={actionLoading === 'link' ? <CircularProgress size={18} /> : <LinkIcon />}
          >
            Link
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UniFiDeviceLinking;
